import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Container,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  ArrowBack,
  Close,
} from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import api from "../api/api";
import CustomSnackbar from "../components/CustomSnackbar";
import en_ja from "../en-ja-map";

const theme = createTheme({
  palette: {
    primary: {
      main: "#ff0000",
    },
  },
});

const Settings = () => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");

  // Modal states
  const [passwordChangeOpen, setPasswordChangeOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState("sendOTP"); // 'sendOTP', 'resetPassword'

  // Form data
  const [formData, setFormData] = useState({
    email: "",
    code: ["", "", "", "", "", ""],
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Get user email from localStorage on component mount
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user.email) {
      setFormData((prev) => ({
        ...prev,
        email: user.email,
      }));
    }
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbarType("success");
    setSnackbarOpen(false);
    setSnackbarMessage("");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length <= 1) {
      const newCode = [...formData.code];
      newCode[index] = value;
      setFormData((prev) => ({
        ...prev,
        code: newCode,
      }));

      // Auto focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(
          `password-code-input-${index + 1}`
        );
        if (nextInput) nextInput.focus();
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (currentStep === "sendOTP") {
      if (!formData.email.trim()) {
        newErrors.email = en_ja["Email is required"];
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = en_ja["Email is invalid"];
      }
    } else if (currentStep === "resetPassword") {
      // Combined OTP + password validation
      const codeString = formData.code.join("");
      if (codeString.length !== 6) {
        newErrors.code = en_ja["Please enter all 6 digits"];
      }

      if (!formData.newPassword.trim()) {
        newErrors.newPassword = en_ja["New password is required"];
      } else if (formData.newPassword.length < 6) {
        newErrors.newPassword = en_ja["Password must be at least 6 characters"];
      }

      if (!formData.confirmPassword.trim()) {
        newErrors.confirmPassword = en_ja["Confirm password is required"];
      } else if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = en_ja["Passwords do not match"];
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendOTPForPasswordChange = async () => {
    setLoading(true);
    try {
      const response = await api.post("v1/clinic/forgot-password", {
        email: formData.email,
      });

      if (response.status === 200) {
        setSnackbarMessage(en_ja["OTP sent to your email successfully"]);
        setSnackbarType("success");
        setSnackbarOpen(true);
        setCurrentStep("resetPassword");
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        en_ja["Failed to send OTP. Please try again."];
      setSnackbarMessage(message);
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async () => {
    setLoading(true);
    const codeString = formData.code.join("");

    try {
      const response = await api.post("v1/clinic/confirm-forgot-password", {
        email: formData.email,
        code: codeString,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });

      if (response.status === 200) {
        setSnackbarMessage(en_ja["Password changed successfully!"]);
        setSnackbarType("success");
        setSnackbarOpen(true);

        // Close modal and reset form after 2 seconds
        setTimeout(() => {
          handleCloseModal();
        }, 2000);
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        en_ja["Failed to change password. Please try again."];
      setSnackbarMessage(message);
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (currentStep === "sendOTP") {
      await sendOTPForPasswordChange();
    } else if (currentStep === "resetPassword") {
      await changePassword();
    }
  };

  const handleOpenPasswordChange = () => {
    setPasswordChangeOpen(true);
    setCurrentStep("sendOTP");
    // Reset form
    setFormData((prev) => ({
      ...prev,
      code: ["", "", "", "", "", ""],
      newPassword: "",
      confirmPassword: "",
    }));
    setErrors({});
  };

  const handleCloseModal = () => {
    setPasswordChangeOpen(false);
    setCurrentStep("sendOTP");
    setFormData((prev) => ({
      ...prev,
      code: ["", "", "", "", "", ""],
      newPassword: "",
      confirmPassword: "",
    }));
    setErrors({});
  };

  const handleBackStep = () => {
    if (currentStep === "resetPassword") {
      setCurrentStep("sendOTP");
    }
    setErrors({});
  };

  const renderSendOTPStep = () => (
    <>
      <Typography
        variant="h5"
        sx={{
          mb: 1,
          fontWeight: 600,
          color: "#1a1a1a",
          textAlign: "center",
        }}
      >
        {en_ja["Change Password"]}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          mb: 4,
          color: "#666666",
          textAlign: "center",
          fontSize: "14px",
        }}
      >
        {en_ja["We'll send you an OTP to verify your identity"]}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: "#1a1a1a",
              fontWeight: 500,
              fontSize: "14px",
            }}
          >
            {en_ja["Email Address"]}
          </Typography>
          <TextField
            fullWidth
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            variant="outlined"
            disabled={true} // Email should be blocked as per requirements
            error={!!errors.email}
            helperText={errors.email}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: "14px",
                backgroundColor: "#f8f9fa",
                "& fieldset": {
                  borderColor: "#e0e0e0",
                },
              },
            }}
          />
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            py: 1.5,
            backgroundColor: "#ff0000",
            borderRadius: 2,
            textTransform: "none",
            fontSize: "16px",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#e60000",
              boxShadow: "none",
            },
            "&:disabled": {
              backgroundColor: "#cccccc",
            },
          }}
        >
          {loading ? en_ja["Sending OTP..."] : en_ja["Send OTP"]}
        </Button>
      </form>
    </>
  );

  const renderResetPasswordStep = () => (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton onClick={handleBackStep} sx={{ mr: 1, p: 0.5 }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: "#1a1a1a",
            flex: 1,
            textAlign: "center",
          }}
        >
          {en_ja["Reset Password"]}
        </Typography>
      </Box>

      <Typography
        variant="body2"
        sx={{
          mb: 4,
          color: "#666666",
          textAlign: "center",
          fontSize: "14px",
        }}
      >
        {en_ja["Enter the code sent to your email and set your new password"]}
      </Typography>

      <form onSubmit={handleSubmit}>
        {/* OTP Input */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: "#1a1a1a",
              fontWeight: 500,
              fontSize: "14px",
            }}
          >
            {en_ja["Verification Code"]}
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "center",
            }}
          >
            {formData.code.map((digit, index) => (
              <TextField
                key={index}
                id={`password-code-input-${index}`}
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                variant="outlined"
                error={!!errors.code}
                inputProps={{
                  maxLength: 1,
                  style: {
                    textAlign: "center",
                    fontSize: "16px",
                    fontWeight: 600,
                  },
                }}
                sx={{
                  width: "45px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "& fieldset": {
                      borderColor: errors.code ? "#d32f2f" : "#e0e0e0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#ff0000",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#ff0000",
                    },
                  },
                }}
              />
            ))}
          </Box>
          {errors.code && (
            <Typography
              variant="caption"
              sx={{
                color: "#d32f2f",
                display: "block",
                textAlign: "center",
                mt: 1,
              }}
            >
              {errors.code}
            </Typography>
          )}
        </Box>

        {/* New Password */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: "#1a1a1a",
              fontWeight: 500,
              fontSize: "14px",
            }}
          >
            {en_ja["New Password"]}
          </Typography>
          <TextField
            fullWidth
            name="newPassword"
            type={showNewPassword ? "text" : "password"}
            value={formData.newPassword}
            onChange={handleInputChange}
            placeholder={en_ja["Enter your new password"]}
            variant="outlined"
            error={!!errors.newPassword}
            helperText={errors.newPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                    size="small"
                  >
                    {showNewPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: "14px",
                "& fieldset": {
                  borderColor: "#e0e0e0",
                },
                "&:hover fieldset": {
                  borderColor: "#ff0000",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ff0000",
                },
              },
            }}
          />
        </Box>

        {/* Confirm Password */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: "#1a1a1a",
              fontWeight: 500,
              fontSize: "14px",
            }}
          >
            {en_ja["Confirm Password"]}
          </Typography>
          <TextField
            fullWidth
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            value={formData.confirmPassword}
            onChange={handleInputChange}
            placeholder={en_ja["Confirm your new password"]}
            variant="outlined"
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    size="small"
                  >
                    {showConfirmPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: "14px",
                "& fieldset": {
                  borderColor: "#e0e0e0",
                },
                "&:hover fieldset": {
                  borderColor: "#ff0000",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ff0000",
                },
              },
            }}
          />
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            py: 1.5,
            backgroundColor: "#ff0000",
            borderRadius: 2,
            textTransform: "none",
            fontSize: "16px",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#e60000",
              boxShadow: "none",
            },
            "&:disabled": {
              backgroundColor: "#cccccc",
            },
          }}
        >
          {loading ? en_ja["Changing Password..."] : en_ja["Reset Password"]}
        </Button>
      </form>
    </>
  );

  return (
    <ThemeProvider theme={theme}>
      <CustomSnackbar
        isOpen={snackbarOpen}
        message={snackbarMessage}
        onClose={handleCloseSnackbar}
        type={snackbarType}
      />

      <Container maxWidth="md" sx={{ py: 4 }}>

        {/* Password Change Section */}
        <Card
          elevation={2}
          sx={{
            p: 3,
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            mb: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: "#1a1a1a",
            }}
          >
            {en_ja["Password Change"]}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: "#666666",
                  fontSize: "14px",
                  mb: 0.5,
                }}
              >
                {en_ja["Current Password"]}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#1a1a1a",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                •••••••••••••
              </Typography>
            </Box>

            <Button
              variant="outlined"
              onClick={handleOpenPasswordChange}
              sx={{
                textTransform: "none",
                borderColor: "#ff0000",
                color: "#ff0000",
                fontWeight: 600,
                borderRadius: 2,
                px: 3,
                "&:hover": {
                  borderColor: "#e60000",
                  backgroundColor: "#fff5f5",
                },
              }}
            >
              {en_ja["Change Password"]}
            </Button>
          </Box>
        </Card>

        {/* Password Change Modal */}
        <Dialog
          open={passwordChangeOpen}
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 1,
            },
          }}
        >
          <DialogContent sx={{ p: 4, position: "relative" }}>
            {/* Close button */}
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: "absolute",
                right: 16,
                top: 16,
                color: "#666666",
              }}
            >
              <Close fontSize="small" />
            </IconButton>

            {/* Render appropriate step */}
            {currentStep === "sendOTP" && renderSendOTPStep()}
            {currentStep === "resetPassword" && renderResetPasswordStep()}
          </DialogContent>
        </Dialog>
      </Container>
    </ThemeProvider>
  );
};

export default Settings;